'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import {
  CheckCircle, Calendar, MapPin, Users, Music, Clock, DollarSign, FileText,
  AlertCircle, ChevronRight, Phone, Mail, CreditCard, Loader2, UserPlus,
} from 'lucide-react';
import StripePaymentModal from './StripePaymentModal';
import DepositSuccessConfirmation, { DepositSuccessData } from './DepositSuccessConfirmation';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  BASE_DJ_PRICE,
  SERVICE_PRICES,
  calculateBookingQuote,
  getBalanceDueDateISO,
} from '@/lib/booking/pricing';

interface BookingConfirmationData {
  name: string;
  email: string;
  phone: string;
  eventType: string;
  eventDate: string;
  venue: string;
  guestCount: string;
  services: string[];
  message: string;
}

export interface BookingQuoteResult {
  eventId: string;
  leadId: string;
  contractId: string | null;
  depositScheduleId: string | null;
  balanceScheduleId: string | null;
  balanceDueDate: string;
  quote: {
    addOnSubtotal: number;
    coordinationFee: number;
    totalCost: number;
    depositAmount: number;
    balanceDue: number;
  };
}

interface BookingConfirmationScreenProps {
  data: BookingConfirmationData;
  booking: BookingQuoteResult;
  onNewInquiry: () => void;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return 'TBD';
  const [year, month, day] = dateStr.split('-').map(Number);
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return `${months[month - 1]} ${day}, ${year}`;
}

function formatDueDate(dateStr: string): string {
  if (!dateStr) return 'TBD';
  if (dateStr.includes(',')) return dateStr;
  return formatDate(dateStr);
}

type PaymentStatus = 'idle' | 'loading' | 'ready' | 'paid';

interface PaymentState {
  deposit: PaymentStatus;
  balance: PaymentStatus;
}

export default function BookingConfirmationScreen({ data, booking, onNewInquiry }: BookingConfirmationScreenProps) {
  const quote = booking.quote || calculateBookingQuote(data.services);
  const {
    coordinationFee,
    totalCost,
    depositAmount,
    balanceDue,
  } = quote;
  const balanceDueDate = booking.balanceDueDate || getBalanceDueDateISO(data.eventDate);

  const [paymentStatus, setPaymentStatus] = useState<PaymentState>({ deposit: 'idle', balance: 'idle' });
  const [scheduleIds, setScheduleIds] = useState({
    deposit: booking.depositScheduleId,
    balance: booking.balanceScheduleId,
  });
  const [clientSecrets, setClientSecrets] = useState<{ deposit: string | null; balance: string | null }>({
    deposit: null,
    balance: null,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [activePaymentType, setActivePaymentType] = useState<'deposit' | 'balance'>('deposit');
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [showDepositSuccess, setShowDepositSuccess] = useState(false);

  const { user } = useAuth();
  const isLoggedIn = !!user;

  const eventDescription = `${data.eventType} — ${formatDate(data.eventDate)}${data.venue ? ` @ ${data.venue}` : ''}`;
  const signupHref = `/sign-up-login-screen?role=client&email=${encodeURIComponent(data.email)}`;
  const portalHref = `/client-portal`;

  const initiatePayment = async (type: 'deposit' | 'balance') => {
    const amount = type === 'deposit' ? depositAmount : balanceDue;
    const label = type === 'deposit' ? 'Deposit (25%)' : 'Balance Due';

    setPaymentStatus(prev => ({ ...prev, [type]: 'loading' }));
    setPaymentError(null);

    try {
      const supabase = createClient();
      let scheduleId = scheduleIds[type];

      // Create schedule only if inquiry API didn't already create one
      if (!scheduleId) {
        const { data: schedule, error: scheduleError } = await supabase
          .from('payment_schedules')
          .insert({
            client_id: null,
            event_id: booking.eventId || null,
            label,
            amount,
            due_date: type === 'deposit' ? new Date().toISOString().split('T')[0]
              : balanceDueDate,
            status: 'pending',
            notes: `Guest booking: ${data.name} | ${eventDescription}`,
          })
          .select('id')
          .single();

        if (scheduleError || !schedule) {
          throw new Error(scheduleError?.message ?? 'Failed to create payment record');
        }
        scheduleId = schedule.id;
        setScheduleIds(prev => ({ ...prev, [type]: scheduleId }));
      }

      const { data: intentData, error: intentError } = await supabase.functions.invoke<{
        clientSecret: string;
        paymentIntentId: string;
      }>('create-payment-intent', {
        body: {
          paymentScheduleId: scheduleId,
          userId: null,
          customerName: data.name,
          customerEmail: data.email,
          billingAddress: {
            line1: data.venue || '123 Event Venue',
            city: 'Merrillville',
            state: 'IN',
            postal_code: '46410',
            country: 'US',
          },
        },
      });

      if (intentError || !intentData?.clientSecret) {
        const errMsg = (intentData as any)?.error ?? intentError?.message ?? 'Payment setup failed';
        throw new Error(errMsg);
      }

      setClientSecrets(prev => ({ ...prev, [type]: intentData.clientSecret }));
      setPaymentStatus(prev => ({ ...prev, [type]: 'ready' }));
      setActivePaymentType(type);
      setModalOpen(true);
    } catch (err: any) {
      console.error('Payment initiation error:', err);
      setPaymentError(err?.message ?? 'Unable to start payment. Please try again.');
      setPaymentStatus(prev => ({ ...prev, [type]: 'idle' }));
    }
  };

  const handlePaymentSuccess = (type: 'deposit' | 'balance') => {
    setModalOpen(false);
    setPaymentStatus(prev => ({ ...prev, [type]: 'paid' }));
    if (type === 'deposit') {
      setShowDepositSuccess(true);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setPaymentStatus(prev => ({
      deposit: prev.deposit === 'paid' ? 'paid' : 'idle',
      balance: prev.balance === 'paid' ? 'paid' : 'idle',
    }));
  };

  return (
    <section id="booking" className="py-24 relative">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/15 border border-primary/40 mb-5">
            <CheckCircle size={32} className="text-primary" />
          </div>
          <p className="text-xs font-mono tracking-[0.4em] text-muted-foreground uppercase mb-3">Inquiry Received</p>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 gold-gradient-text">You&apos;re All Set!</h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Your quote is ready below. Create a client account with this email to track bookings, payments, invoices, and reschedule requests.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 flex flex-col gap-6">
            {/* Event Details */}
            <div className="rounded-2xl neon-border-gold p-7 card-glass">
              <h3 className="text-base font-bold text-foreground mb-5 flex items-center gap-2">
                <Calendar size={16} className="text-primary" />
                Event Details
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Client</span>
                  <span className="text-sm font-semibold text-foreground">{data.name}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Event Type</span>
                  <span className="text-sm font-semibold text-foreground">{data.eventType}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Event Date</span>
                  <span className="text-sm font-semibold text-primary">{formatDate(data.eventDate)}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Guest Count</span>
                  <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                    <Users size={13} className="text-muted-foreground" />
                    {data.guestCount || 'TBD'}
                  </span>
                </div>
                {data.venue && (
                  <div className="flex flex-col gap-1 sm:col-span-2">
                    <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Venue</span>
                    <span className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                      <MapPin size={13} className="text-muted-foreground" />
                      {data.venue}
                    </span>
                  </div>
                )}
              </div>
              <div className="mt-5 pt-5 border-t border-border/40 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Mail size={13} className="text-primary/70 shrink-0" />
                  <span className="truncate">{data.email}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Phone size={13} className="text-primary/70 shrink-0" />
                  <span>{data.phone}</span>
                </div>
              </div>
            </div>

            {data.services.length > 0 && (
              <div className="rounded-2xl card-glass p-7">
                <h3 className="text-base font-bold text-foreground mb-5 flex items-center gap-2">
                  <Music size={16} className="text-accent" />
                  Requested Add-On Services
                  <span className="ml-auto text-xs font-mono text-muted-foreground">Vendors being contacted</span>
                </h3>
                <div className="flex flex-col gap-2">
                  {data.services.map(service => (
                    <div key={service} className="flex items-center justify-between py-2.5 px-4 rounded-lg bg-accent/5 border border-accent/15">
                      <div className="flex items-center gap-2.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-accent" />
                        <span className="text-sm font-medium text-foreground">{service}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-muted-foreground font-mono">Est. ${(SERVICE_PRICES[service] || 0).toLocaleString()}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/10 border border-yellow-500/25 text-yellow-400 font-mono">Pending</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-muted-foreground flex items-start gap-1.5">
                  <AlertCircle size={12} className="text-accent shrink-0 mt-0.5" />
                  Vendor availability and final rates confirmed within 24 hours.
                </p>
              </div>
            )}

            {/* Contract Terms */}
            <div className="rounded-2xl card-glass p-7">
              <h3 className="text-base font-bold text-foreground mb-5 flex items-center gap-2">
                <FileText size={16} className="text-primary" />
                Contract Terms
              </h3>
              <div className="flex flex-col gap-3">
                {[
                  { label: 'Deposit', detail: `25% ($${depositAmount.toLocaleString()}) due to secure your date` },
                  { label: 'Balance Due', detail: `$${balanceDue.toLocaleString()} by ${formatDueDate(balanceDueDate)} (14 days before event)` },
                  { label: 'Cancellation', detail: 'Deposit non-refundable within 30 days of event' },
                  { label: 'Vendor Coordination', detail: '20% coordination fee applied to all vendor rates' },
                  { label: 'Overtime', detail: '$150/hr beyond contracted event hours' },
                  { label: 'Travel', detail: 'Events beyond 50 miles may incur a travel fee' },
                ].map(term => (
                  <div key={term.label} className="flex items-start gap-3 py-2.5 border-b border-border/30 last:border-0">
                    <ChevronRight size={14} className="text-primary shrink-0 mt-0.5" />
                    <div>
                      <span className="text-sm font-semibold text-foreground">{term.label}: </span>
                      <span className="text-sm text-muted-foreground">{term.detail}</span>
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-4 text-xs text-muted-foreground">
                A draft contract is on file for this booking. Formal signing will be completed with Christopher — copy sent to{' '}
                <span className="text-primary">{data.email}</span>.
              </p>
            </div>
          </div>

          {/* Cost + payments */}
          <div className="flex flex-col gap-6">
            <div className="rounded-2xl neon-border-gold p-6 card-glass sticky top-6">
              <h3 className="text-base font-bold text-foreground mb-5 flex items-center gap-2">
                <DollarSign size={16} className="text-primary" />
                Estimated Cost
              </h3>

              <div className="flex flex-col gap-3 mb-5">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">DJ Services</span>
                  <span className="text-foreground font-mono font-semibold">${BASE_DJ_PRICE.toLocaleString()}</span>
                </div>
                {data.services.map(service => (
                  <div key={`cost-${service}`} className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{service}</span>
                    <span className="text-foreground font-mono">${(SERVICE_PRICES[service] || 0).toLocaleString()}</span>
                  </div>
                ))}
                {coordinationFee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Coordination Fee (20%)</span>
                    <span className="text-foreground font-mono">${coordinationFee.toLocaleString()}</span>
                  </div>
                )}
                <div className="border-t border-border/50 pt-3 flex justify-between">
                  <span className="text-sm font-bold text-foreground">Estimated Total</span>
                  <span className="text-base font-extrabold gold-gradient-text font-mono">${totalCost.toLocaleString()}</span>
                </div>
              </div>

              <div className="rounded-xl bg-primary/5 border border-primary/20 p-4 flex flex-col gap-3 mb-4">
                <p className="text-xs font-mono text-primary uppercase tracking-wider mb-1">Payment Schedule</p>

                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Deposit Due Now</p>
                    <p className="text-xs text-muted-foreground">To secure your date</p>
                  </div>
                  <span className="text-sm font-bold text-primary font-mono">${depositAmount.toLocaleString()}</span>
                </div>

                {paymentStatus.deposit === 'paid' ? (
                  <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-green-500/10 border border-green-500/25">
                    <CheckCircle size={14} className="text-green-400 shrink-0" />
                    <span className="text-xs font-semibold text-green-400">Deposit Paid</span>
                  </div>
                ) : (
                  <button
                    onClick={() => initiatePayment('deposit')}
                    disabled={paymentStatus.deposit === 'loading' || paymentStatus.deposit === 'ready'}
                    className="w-full py-2.5 rounded-xl text-sm font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {paymentStatus.deposit === 'loading' ? (
                      <><Loader2 size={14} className="animate-spin" /> Setting up...</>
                    ) : (
                      <><CreditCard size={14} /> Pay Deposit — ${depositAmount.toLocaleString()}</>
                    )}
                  </button>
                )}

                <div className="border-t border-primary/15 pt-3 flex justify-between items-center">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Balance Due</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <Clock size={11} className="text-muted-foreground" />
                      <p className="text-xs text-muted-foreground">{formatDueDate(balanceDueDate)}</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-foreground font-mono">${balanceDue.toLocaleString()}</span>
                </div>

                {paymentStatus.balance === 'paid' ? (
                  <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-green-500/10 border border-green-500/25">
                    <CheckCircle size={14} className="text-green-400 shrink-0" />
                    <span className="text-xs font-semibold text-green-400">Balance Paid</span>
                  </div>
                ) : (
                  <button
                    onClick={() => initiatePayment('balance')}
                    disabled={paymentStatus.balance === 'loading' || paymentStatus.balance === 'ready'}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold border border-border text-muted-foreground hover:border-primary/40 hover:text-foreground transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {paymentStatus.balance === 'loading' ? (
                      <><Loader2 size={14} className="animate-spin" /> Setting up...</>
                    ) : (
                      <><CreditCard size={14} /> Pay Balance — ${balanceDue.toLocaleString()}</>
                    )}
                  </button>
                )}
              </div>

              {paymentError && (
                <div className="rounded-lg bg-red-500/10 border border-red-500/30 px-3 py-2.5 mb-3">
                  <p className="text-xs text-red-400">{paymentError}</p>
                </div>
              )}

              <p className="text-xs text-muted-foreground text-center leading-relaxed">
                Final pricing confirmed after Christopher reviews. Estimates use standard rates.
              </p>
            </div>

            {/* Track booking CTA — only show if not already logged in */}
            {!isLoggedIn ? (
              <div className="rounded-2xl card-glass-cyan p-6">
                <h3 className="text-base font-bold text-foreground mb-4">Track This Booking</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Sign up with <span className="text-foreground font-medium">{data.email}</span> to view this event, download invoices, track payments, and request reschedules.
                </p>
                <Link
                  href={signupHref}
                  className="w-full py-3 rounded-xl text-sm font-bold gold-gradient text-black hover:opacity-90 transition-all flex items-center justify-center gap-2"
                >
                  <UserPlus size={15} />
                  Create Client Account
                </Link>
              </div>
            ) : (
              <div className="rounded-2xl card-glass-cyan p-6">
                <h3 className="text-base font-bold text-foreground mb-4">View Your Booking</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Your inquiry has been saved to your account. Head to your client portal to track payments, sign your contract, and manage your event.
                </p>
                <Link
                  href={portalHref}
                  className="w-full py-3 rounded-xl text-sm font-bold gold-gradient text-black hover:opacity-90 transition-all flex items-center justify-center gap-2"
                >
                  <CheckCircle size={15} />
                  Go to Client Portal
                </Link>
              </div>
            )}

            <div className="rounded-2xl card-glass p-6">
              <h3 className="text-base font-bold text-foreground mb-4">What Happens Next</h3>
              <div className="flex flex-col gap-3">
                {[
                  { step: '01', text: 'Christopher reviews your inquiry within 2 hours' },
                  { step: '02', text: 'Create your client account to manage the booking' },
                  { step: '03', text: 'Vendor availability confirmed within 24 hours' },
                  { step: '04', text: 'Pay deposit & sign contract to lock in your date' },
                ].map(item => (
                  <div key={item.step} className="flex items-start gap-3">
                    <span className="text-xs font-mono text-accent font-bold shrink-0 mt-0.5">{item.step}</span>
                    <p className="text-sm text-muted-foreground">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={onNewInquiry}
              className="w-full py-3 rounded-xl text-sm font-semibold border border-border text-muted-foreground hover:border-primary/40 hover:text-foreground transition-all duration-200"
            >
              Submit Another Inquiry
            </button>
          </div>
        </div>
      </div>

      {modalOpen && clientSecrets[activePaymentType] && (
        <StripePaymentModal
          isOpen={modalOpen}
          onClose={handleModalClose}
          onSuccess={handlePaymentSuccess}
          paymentType={activePaymentType}
          amount={activePaymentType === 'deposit' ? depositAmount : balanceDue}
          clientSecret={clientSecrets[activePaymentType]!}
          paymentScheduleId={scheduleIds[activePaymentType] ?? ''}
          eventDescription={eventDescription}
          clientEmail={data.email}
          clientName={data.name}
        />
      )}

      {showDepositSuccess && (
        <DepositSuccessConfirmation
          data={{
            clientName: data.name,
            clientEmail: data.email,
            clientPhone: data.phone,
            eventType: data.eventType,
            eventDate: data.eventDate,
            venue: data.venue,
            services: data.services,
            totalCost,
            depositPaid: depositAmount,
            balanceDue,
            balanceDueDate,
            vendorName: 'Christopher Harris (DJ ExXtra)',
          }}
          onClose={() => setShowDepositSuccess(false)}
        />
      )}
    </section>
  );
}
